import writeXlsxFile from 'write-excel-file/browser';
import type { Row } from 'write-excel-file/browser';
import { sanitiseFilename } from '@/features/analytics/utils/exportUtils';
import { cell, columnWidths, headerCell } from '@/lib/xlsxSheet';
import type { SheetContent } from '@/lib/xlsxSheet';
import type { GaExperimentResult } from '../types/ToolRecommenderTypes';
import { generatedFieldKeys } from './experimentView';

/** Context column headers, already translated by the caller. */
export interface ExperimentSheetHeaders {
  rank: string;
  material: string;
  operation: string;
  baseToolArticle: string;
  baseScore: string;
  generatedScore: string;
}

/** Header row (bold) followed by one row per generated tool. */
export type ExperimentSheet = SheetContent;

/**
 * Wide-format sheet: one row per generated tool (one per stored comparison) — the
 * comparison's context and scores first, then one column per visible characteristic
 * (union across all results, canonical ISO-13399 order; empty when a result lacks it).
 */
export function buildExperimentSheet(
  result: GaExperimentResult,
  headers: ExperimentSheetHeaders,
  labelFor: (key: string) => string
): ExperimentSheet {
  const comparisons = result.resultComparisons;
  const fieldKeys = generatedFieldKeys(comparisons);
  const headerRow: Row = [
    headers.rank,
    headers.material,
    headers.operation,
    headers.baseToolArticle,
    headers.baseScore,
    headers.generatedScore,
    ...fieldKeys.map(labelFor),
  ].map(headerCell);
  const dataRows: Row[] = comparisons.map((row, index) =>
    [
      row.rank ?? index + 1,
      row.materialName,
      row.operationName,
      row.baseToolArticle,
      row.baseScore,
      row.score,
      ...fieldKeys.map((key) => (row.generatedTool ?? {})[key]),
    ].map(cell)
  );
  const rows = [headerRow, ...dataRows];
  return { rows, columns: columnWidths(rows) };
}

export function experimentXlsxFilename(experimentId: string): string {
  return `${sanitiseFilename(`experiment-${experimentId}`)}.xlsx`;
}

/** Builds the workbook and hands it to the browser as a download. */
export async function exportExperimentXlsx(
  result: GaExperimentResult,
  headers: ExperimentSheetHeaders,
  labelFor: (key: string) => string
): Promise<void> {
  const { rows, columns } = buildExperimentSheet(result, headers, labelFor);
  await writeXlsxFile(rows, { columns, stickyRowsCount: 1 }).toFile(
    experimentXlsxFilename(result.experimentId)
  );
}
